import { useCallback, useEffect, useRef, useState } from 'react'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import { exposeStrapiError } from 'utils'
import {
  ApproveTrainingCertificate,
  ReadTrainingApproval,
  RejectTrainingCertificate,
} from 'services/training-approval'

export default function useController() {
  const { id } = useParams()
  const history = useHistory()
  const location = useLocation()
  const rejectionFormRef = useRef()
  const [approval, setApproval] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [action, setAction] = useState('')
  const [decision, setDecision] = useState('')
  const [feedback, setFeedback] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    const result = await ReadTrainingApproval(id)
    if (!result.ok) {
      setError(result.status === 403
        ? 'You are not authorized to review this certification request.'
        : 'This certification request could not be loaded.')
      if (result.status !== 403) exposeStrapiError(result.data)
    } else {
      setApproval(result.data?.data || null)
      setError('')
    }
    setLoading(false)
  }, [id])

  useEffect(() => { load() }, [load])

  const decide = useCallback(async () => {
    if (!decision) return
    const reason = rejectionFormRef.current?.getForm(true)?.reviewNote || ''
    setAction(decision)
    const result = decision === 'approve'
      ? await ApproveTrainingCertificate(id)
      : await RejectTrainingCertificate(id, reason)
    if (!result.ok) {
      exposeStrapiError(result.data)
    } else {
      setApproval(result.data?.data || null)
      setFeedback(decision)
      setDecision('')
      toast.success(decision === 'approve'
        ? 'Certification approved and certificate issued.'
        : 'Certification request rejected.')
    }
    setAction('')
  }, [decision, id])

  const back = useCallback(() => {
    history.push(location.pathname.replace(new RegExp(`/${id}/?$`), ''))
  }, [history, id, location.pathname])

  return {
    approval,
    loading,
    error,
    action,
    decision,
    feedback,
    rejectionFormRef,
    retry: load,
    onBack: back,
    onApprove: () => setDecision('approve'),
    onReject: () => setDecision('reject'),
    onCancelDecision: () => setDecision(''),
    onConfirmDecision: decide,
  }
}
